import { ChangeDetectorRef, Injectable } from '@angular/core';
import { MenuItemModel } from 'app/_models/menuitem.model';
import { MenuListItem } from 'app/_models/menulistitem.model';
import { NavigationService } from './navigation.service';
import { UserRoles } from 'app/_models/userroles';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { MenuModel } from 'app/_models/menu.model';
import { ReplaySubject } from 'rxjs';
import { AuthService } from './auth.service';
import { NavigationEnd, Params, Router } from '@angular/router';
import { Location } from '@angular/common';
import { each } from 'lodash';
import { UserRolesService } from './userroles.service';

function paramsEqual(input: string, params: null | Params) {
  if (params == null) return !input;
  if (!input) return false;
  const map = new URLSearchParams();
  for (const key in params) map.set(key, params[key]);
  return input === map.toString();
}

function findActiveMenu(location: string, item: FuseNavigationItem): null | FuseNavigationItem[] {
  const [path, params] = location.split('?');
  const checkedPath = path.includes('%20') ? decodeURIComponent(path) : path;
  const stack: FuseNavigationItem[] = [item];
  return impl(item);

  function impl(parent: FuseNavigationItem) {
    for (const item of parent.children) {
      if (item.link && item.link.includes('catalogues') && path.includes('catalogues') && path == item.link) {
        stack.push(item);
        return stack;
      } else if (item.link == checkedPath && paramsEqual(params, item.queryParams)) {
        stack.push(item);
        return stack;
      }

      if (item.children && item.children.length) {
        stack.push(item);
        const result = impl(item);
        if (result) return result;
        stack.pop();
      }
    }
    return null;
  }
}

type SelectedMenu = null | readonly FuseNavigationItem[];

@Injectable({ providedIn: 'root' })
export class MenuService {
  private _menuValue: RenderedMenu;
  private _menu = new ReplaySubject<RenderedMenu>(1);
  private _approvals = new ReplaySubject<any>(1);
  private _selectedValue: SelectedMenu;
  private _selected = new ReplaySubject<SelectedMenu>(1);
  private approvalIndex = 0;
  private __selected = new ReplaySubject<null | readonly FuseNavigationItem[]>(1);
  private readonly operationIconMap: Record<string, string> = {
    OPENFORMSLIST: 'assignment', // List of forms - clipboard
    OPENFORM: 'article', // Single form - single document
    OPENREPORT: 'bar_chart', // Reporting - bar chart
    OPENVIEW: 'pageview', // View (details) - magnifying glass over page
    OPENJOB: 'work', // Single job - briefcase
    OPENMAP: 'heroicons_outline:map', // Open map - map
    OPENJOBLIST: 'list_alt', // Job list - list with detail
    OPENSCHEDULE: 'calendar_today', // Scheduling - calendar
    OPENNOTIFICATIONHISTORY: 'notifications', // Notification history - notification bell
    OPENMEDIAMANAGEMENT: 'collections', // Media management - photo collection
    OPENCATALOGUE: 'label', // Catalogue - warehouse shelf
    OPENMOBILEUSERSLIST: 'group', // Users - group of people
    OPENEXTERNALWEBPAGE: 'launch', // External link - launch arrow
    OPENRECORDEDTEMPLATES: 'description', // Templates - description/file
    OPENLONEWORKERCONFIGURATION: 'settings', // lone worker configuration icon
    OPENLONEWORKEREVENTS: 'event_available', // lone worker event icon
    OPENLONEWORKERREPORTS: 'assessment', // lone worker reports icon
    OPENLOCATIONTRACKING: 'my_location', // location_searching// track worker icon
  };
  customMenu: boolean = false;
  isApprovalMenu: boolean = false;

  public get menu() {
    return this._menu.asObservable();
  }
  public get approvals() {
    return this._approvals.asObservable();
  }
  public get selected() {
    return this.__selected.asObservable();
  }

  constructor(
    public nav: NavigationService,
    router: Router,
    private _location: Location,
    private _authService: AuthService,
    private _userRolesService: UserRolesService,
  ) {
    nav.menu$.subscribe((menus) => {
      const ids = menus.map(({ userGroupDetails }) => userGroupDetails.menuId);
      const filtered = menus.filter(({ userGroupDetails }, index) => !ids.includes(userGroupDetails.menuId, index + 1));
      const sorted = filtered.sort((a, b) => {
        const an = a.menuList.find((o) => o.menuId == a.userGroupDetails.menuId).name;
        const bn = b.menuList.find((o) => o.menuId == b.userGroupDetails.menuId).name;
        return an > bn ? 1 : -1;
      });

      const renderedMenu = this.convertMenu(sorted);

      if (renderedMenu.root.title == 'Static Menu' || (this.customMenu && this.isApprovalMenu)) {
        nav.approvals$.subscribe((appvols) => {
          if (appvols && appvols.length > 0) {
            const parentObj: any[] = [];

            appvols.forEach((element: any) => {
              const statusDetail = element?.Userform?.StatusDetail ?? [];

              // Per-form total
              const totalCount = statusDetail.reduce((sum: number, s: any) => sum + (Number(s?.ApprovalCount) || 0), 0);

              const pObj: any = {
                title: element.Userform.Name, // keep plain title
                icon: 'folder',
                type: 'collapsable',
                // Per-form badge (round, 99+ cap)
                badge:
                  totalCount > 0
                    ? {
                        title: totalCount > 99 ? '99+' : String(totalCount),
                        classes: 'ml-2 inline-flex items-right justify-center w-7 h-7 rounded-full text-[9px] font-semibold bg-primary text-white',
                      }
                    : undefined,
                children: [] as any[],
              };

              const childObj: any[] = statusDetail.map((child: any) => {
                const count = Number(child?.ApprovalCount) || 0;
                return {
                  title: child.ApprovalText, // keep plain title
                  link: `/forms/${element.Userform.UserFormId}/completedrecords/approval/${child.ApprovalText}`,
                  operation: 'OPENFORM',
                  icon: 'assignment',
                  type: 'basic',
                  // Child badge (round, 99+ cap)
                  badge:
                    count > 0
                      ? {
                          title: count > 99 ? '99+' : String(count),
                          classes: 'ml-2 inline-flex items-right justify-center w-7 h-7 rounded-full text-[9px] font-semibold bg-primary text-white',
                        }
                      : undefined,
                  // hidden: !this._userRolesService.getUserRole(UserRoles.Jobs_View),
                };
              });

              pObj.children = childObj;
              parentObj.push(pObj);
            });

            // Find the Approvals node (fallback to last if not found)
            let approvalsNode = renderedMenu.root.children.filter((n: any) => (n?.operation ?? '').toUpperCase().startsWith('OPENAPPROVAL'));
            if (approvalsNode.length == 0) approvalsNode = [renderedMenu.root.children[renderedMenu.root.children.length - 1]];
            // Attach built parents
            approvalsNode.forEach((apNode, index) => {
              apNode.id = apNode.id + index;
              apNode.children = [...parentObj];

              // Grand total for top-level Approvals badge
              const grandTotal = (appvols ?? []).reduce((acc: number, el: any) => {
                const sd = el?.Userform?.StatusDetail ?? [];
                const perForm = sd.reduce((s: number, x: any) => s + (Number(x?.ApprovalCount) || 0), 0);
                return acc + perForm;
              }, 0);

              // Keep base title & apply/clear badge (round, 99+ cap)
              apNode.title = (apNode.title ?? 'Approvals').replace(/\s*\(\d+\)\s*$/, '');
              apNode.badge =
                grandTotal > 0
                  ? {
                      title: grandTotal > 99 ? '99+' : String(grandTotal),
                      classes: 'ml-2 inline-flex items-center justify-center w-7 h-7 rounded-full text-[9px] font-semibold bg-indigo-600 text-white',
                    }
                  : undefined;
            });
            this.setMenu(renderedMenu);
          } else {
            this.setMenu(renderedMenu);
          }
        });
      } else {
        this.setMenu(renderedMenu);
      }
    });

    router.events.subscribe((evt) => {
      if (evt instanceof NavigationEnd) {
        this.updateSelected();
      }
    });
  }

  private getIconForOperation(operation: string): string {
    return this.operationIconMap[operation] || 'assignment';
  }

  private setMenu(value: RenderedMenu) {
    this._menuValue = value;
    this._menu.next(value);
    this.updateSelected();
  }

  private updateSelected() {
    const route = this._location.path();
    const current = findActiveMenu(route, this._menuValue.root);
    this.setSelected(current);
  }

  private setSelected(value: SelectedMenu) {
    if (value && this._selectedValue !== value) {
      let path = value[value.length - 1].link;
      const last = value && this.getFullPath(value[value.length - 1]);
      this._selectedValue = value.filter((e) => e.link == path);
      this.__selected.next(this._selectedValue);
      if (last) localStorage.setItem('lastLocation', last);
    }
  }

  private getFullPath(item: FuseNavigationItem) {
    const { link, queryParams } = item;
    if (queryParams == null) return link;
    const p = new URLSearchParams();
    for (const key in queryParams) p.set(key, queryParams[key]);
    return link + '?' + p;
  }

  private convertMenu(menu: MenuModel[]) {
    let root: FuseNavigationItem = {
      id: 'dashboards',
      title: 'Work Area',
      icon: 'assignment',
      children: [],
      link: '',
      type: 'group',
      operation: '',
    };
    if (menu != null && menu.length > 0) {
      this.customMenu = true;
      this.BuildRenderMenu(menu, root);
      root.children?.push;
    } else {
      this.customMenu = false;
      this.BuildRenderDefaultMenu(menu, root);
      root.title = 'Static Menu';
    }

    return {
      menu,
      customMenu: this.customMenu,
      root,
    };
  }

  private BuildRenderDefaultMenu(menus: MenuModel[], root: FuseNavigationItem) {
    root.children = [
      {
        title: 'Forms',
        link: '/forms',
        operation: 'OPENFORMSLIST',
        icon: this.getIconForOperation('OPENFORMSLIST'),
        type: 'basic',
        hidden: !this._userRolesService.getUserRole(UserRoles.Forms_View),
      },
      {
        title: 'Jobs',
        icon: 'folder',
        type: 'collapsable',
        hidden: !this._userRolesService.getUserRole(UserRoles.Jobs_View),
        children: [
          {
            title: 'List',
            link: '/jobs',
            operation: 'OPENJOBLIST',
            icon: this.getIconForOperation('OPENJOBLIST'),
            type: 'basic',
            hidden: !this._userRolesService.getUserRole(UserRoles.Jobs_View),
          },
          {
            title: 'Schedule',
            link: '/schedule/job-schedule',
            operation: 'OPENSCHEDULE',
            icon: this.getIconForOperation('OPENSCHEDULE'),
            type: 'basic',
            hidden: !this._userRolesService.getUserRole(UserRoles.Jobs_View),
          },
          {
            title: 'Job Assignment',
            type: 'basic',
            link: '/jobs/job-assignment',
            operation: 'OPENJOBASSIGNMENT',
            icon: 'launch',
            hidden: !this._userRolesService.getUserRole(UserRoles.Jobs_View),
          },
          //hide map menu for Static Menu
         /* {
            title: 'Map',
            link: '/jobs/jobmapview',
            operation: 'OPENMAP',
            icon: this.getIconForOperation('OPENMAP'),
            type: 'basic',
            hidden: !this._userRolesService.getUserRole(UserRoles.Jobs_View),
          },
          */
        ],
      },
      {
        title: 'Users',
        link: '/users-list',
        operation: 'OPENUSERSLIST',
        icon: 'view_list',
        type: 'basic',
        hidden: !this._userRolesService.getUserRole(UserRoles.Mobile_Users_View),
      },
      {
        title: 'Notification History',
        type: 'basic',
        link: '/notificationhistory',
        operation: 'OPENNOTIFICATIONHISTORY',
        icon: this.getIconForOperation('OPENNOTIFICATIONHISTORY'),
        hidden: !this._userRolesService.getUserRole(UserRoles.Notification_History_View),
      },
      {
        title: 'Media Management',
        type: 'basic',
        link: '/mediamanagement',
        operation: 'OPENMEDIAMANAGEMENT',
        icon: this.getIconForOperation('OPENMEDIAMANAGEMENT'),
        hidden: !this._userRolesService.getUserRole(UserRoles.Media),
      },
      {
        title: 'Catalogues',
        link: '/catalogues',
        operation: 'OPENCATALOGUE',
        icon: this.getIconForOperation('OPENCATALOGUE'),
        type: 'basic',
        hidden: !this._userRolesService.getUserRole(UserRoles.Catalogues_View),
      },
      {
        title: 'Templates',
        link: '/template',
        operation: 'OPENRECORDEDTEMPLATES',
        icon: this.getIconForOperation('OPENRECORDEDTEMPLATES'),
        type: 'basic',
        hidden: !this._userRolesService.getUserRole(UserRoles.Record_Templates),
      },
      {
        title: 'Lone Worker',
        icon: 'folder',
        type: 'collapsable',
        hidden:
          this._authService.isLoneWorkerEnabled() && !this._userRolesService.getUserRole(UserRoles.Lone_Worker_Configuration) && !this._userRolesService.getUserRole(UserRoles.Lone_Worker_Events),
        children: [
          {
            title: 'Configuration',
            link: '/lone-worker/configuration',
            operation: 'OPENLONEWORKERCONFIGURATION',
            icon: this.getIconForOperation('OPENLONEWORKERCONFIGURATION'),
            type: 'basic',
            // Always shown; access is enforced by the route guard (Access Denied without the role).
          },
          {
            title: 'Events',
            link: '/lone-worker/events',
            operation: 'OPENLONEWORKEREVENTS',
            icon: this.getIconForOperation('OPENLONEWORKEREVENTS'),
            type: 'basic',
            // Always shown; access is enforced by the route guard (Access Denied without the role).
          },
        ],
      },
      {
        title: 'Tracking',
        icon: 'folder',
        type: 'collapsable',
        hidden:
          this._authService.isLoneWorkerEnabled() &&
          !this._userRolesService.getUserRole(UserRoles.Location_Tracking) &&
          !this._userRolesService.getUserRole(UserRoles.Lone_Worker_Reports),

        children: [
          {
            title: 'Configuration',
            link: '/tracking/configuration',
            operation: 'OPENLOCATIONTRACKING',
            icon: this.getIconForOperation('OPENLOCATIONTRACKING'),
            type: 'basic',
            hidden:
              this._authService.isLoneWorkerEnabled() && !this._userRolesService.getUserRole(UserRoles.Location_Tracking),
          },

          {
            title: 'Map View',
            link: '/tracking/map',
            operation: 'OPENLONEWORKERTRACKING',
            icon: this.getIconForOperation('OPENMAP'),
            type: 'basic',
            hidden:
              this._authService.isLoneWorkerEnabled() && !this._userRolesService.getUserRole(UserRoles.Location_Tracking),
          },
          {
            title: 'SSRS Reports',
            link: '/tracking/reports',
            operation: 'OPENLONEWORKERREPORTS',
            icon: this.getIconForOperation('OPENLONEWORKERREPORTS'),
            type: 'basic',
            hidden:true,
          },
          {
            title: 'Reports',
            link: '/tracking/html-reports',
            operation: 'OPENLONEWORKERREPORTS',
            icon: this.getIconForOperation('OPENLONEWORKERREPORTS'),
            type: 'basic',
            hidden:
              this._authService.isLoneWorkerEnabled() &&
              !this._userRolesService.getUserRole(UserRoles.Lone_Worker_Reports),
          },
        ],
      },
      {
        title: 'Approvals',
        icon: 'folder',
        type: 'collapsable',
        operation: 'OPENAPPROVAL',
        // hidden: !this._userRolesService.getUserRole(UserRoles.Jobs_View),
        children: [],
      },
    ];
    root.children = this.appendCrmMenu(root.children);
  }

  private BuildRenderMenu(menus: MenuModel[], root: FuseNavigationItem) {
    for (let i = 0; i < menus.length; i++) {
      const currentItem = menus[i];
      const startMenu = currentItem.menuList.find((x) => x.menuId == currentItem.userGroupDetails.menuId);
      root.title = (startMenu as any).name;
      root.children = this.GenerateMenu(startMenu.menuData, currentItem.menuList);
    }
    root.children.push({
      id: 'dashboards.project',
      title: 'Templates',
      link: '/template',
      operation: 'OPENRECORDEDTEMPLATES',
      icon: this.getIconForOperation('OPENRECORDEDTEMPLATES'),
      type: 'basic',
      children: [],
      hidden: !this._userRolesService.getUserRole(UserRoles.Record_Templates),
    });

    root.children = this.appendCrmMenu(root.children);
  }

  private appendCrmMenu(children: FuseNavigationItem[]) {
    const menuItems = children ?? [];
    const filtered = menuItems.filter((item) => {
      if ((item.title ?? '').toLowerCase() === 'crm') return false;
      return item.link !== '/crm/customers' && item.link !== '/crm/contacts';
    });

    if (!this.isCrmEnabledForCurrentUser()) {
      return filtered;
    }

    filtered.push({
      title: 'CRM',
      icon: 'folder',
      type: 'collapsable',
      children: [
        {
          title: 'Customers',
          link: '/crm/customers',
          operation: 'OPENCRMCUSTOMERS',
          icon: 'assignment',
          type: 'basic',
        },
        {
          title: 'Contacts',
          link: '/crm/contacts',
          operation: 'OPENCRMCONTACTS',
          icon: 'assignment',
          type: 'basic',
        },
      ],
    });

    return filtered;
  }

  private isCrmEnabledForCurrentUser(): boolean {
    const token = localStorage.getItem('accessToken');
    const tokenData = this._authService.getDecodedAccessToken(token);
    const flag = tokenData?.IsCRMEnabled;
    return String(flag).toLowerCase() === 'true';
  }

  private GenerateMenu(menuData: MenuItemModel[], menuList: MenuListItem[]) {
    menuData = menuData['menuItems'];
    const menuArray: RenderMenuItem[] = [];
    for (let i = 0; i < menuData.length; i++) {
      const renderItem: RenderMenuItem = {
        id: 'dashboards.project',
        title: '',
        icon: this.getIconForOperation(menuData[i].operation),
        children: [],
        link: '',
        type: 'basic',
        operation: '',
      };
      if (menuData[i].operation == 'OPENAPPROVAL') {
        this.isApprovalMenu = true;
        this.approvalIndex = i;
        let obj: any = {
          title: menuData[i].name,
          icon: 'folder',
          type: 'collapsable',
          operation: 'OPENAPPROVAL',
          // hidden: !this._userRolesService.getUserRole(UserRoles.Jobs_View),
          children: [],
        };
        menuArray.push(obj);
      }
      if (menuData[i].operation == 'OPENLONEWORKER') {
        let obj: any = {
          title: menuData[i].name,
          icon: 'folder',
          type: 'collapsable',
          operation: 'OPENLONEWORKER',
          children: [],
          hidden:
            this._authService.isLoneWorkerEnabled() && !this._userRolesService.getUserRole(UserRoles.Lone_Worker_Configuration) && !this._userRolesService.getUserRole(UserRoles.Lone_Worker_Events),
        };

        obj.children.push({
          title: 'Configuration',
          link: '/lone-worker/configuration',
          operation: 'OPENLONEWORKERCONFIGURATION',
          icon: this.getIconForOperation('OPENLONEWORKERCONFIGURATION'),
          type: 'basic',
          // Always shown; access is enforced by the route guard (Access Denied without the role).
        });

        obj.children.push({
          title: 'Events',
          link: '/lone-worker/events',
          operation: 'OPENLONEWORKEREVENTS',
          icon: this.getIconForOperation('OPENLONEWORKEREVENTS'),
          type: 'basic',
          // Always shown; access is enforced by the route guard (Access Denied without the role).
        });

        menuArray.push(obj);
        continue;
      }
      if (menuData[i].operation == 'OPENLOCATIONTRACKING') {
        let obj: any = {
          title: menuData[i].name,
          icon: 'folder',
          type: 'collapsable',
          operation: 'OPENLOCATIONTRACKING',
          children: [],
          hidden:
            this._authService.isLoneWorkerEnabled() &&
            !this._userRolesService.getUserRole(UserRoles.Location_Tracking) &&
            !this._userRolesService.getUserRole(UserRoles.Lone_Worker_Reports),
        };

        obj.children.push({
          title: 'Configuration',
          link: '/tracking/configuration',
          operation: 'OPENLOCATIONTRACKING',
          icon: this.getIconForOperation('OPENLOCATIONTRACKING'),
          type: 'basic',
          hidden: this._authService.isLoneWorkerEnabled() && !this._userRolesService.getUserRole(UserRoles.Location_Tracking),
        });

        obj.children.push({
          title: 'Map View',
          link: '/tracking/map',
          operation: 'OPENLONEWORKERTRACKING',
          icon: this.getIconForOperation('OPENMAP'),
          type: 'basic',
          hidden: this._authService.isLoneWorkerEnabled() && !this._userRolesService.getUserRole(UserRoles.Location_Tracking),
        });
        obj.children.push({
          title: 'SSRS Reports',
          link: '/tracking/reports',
          operation: 'OPENLONEWORKERREPORTS',
          icon: this.getIconForOperation('OPENLONEWORKERREPORTS'),
          type: 'basic',
          hidden:true,
        });
        obj.children.push({
          title: 'Reports',
          link: '/tracking/html-reports',
          operation: 'OPENLONEWORKERREPORTS',
          icon: this.getIconForOperation('OPENLONEWORKERREPORTS'),
          type: 'basic',
          hidden:
            this._authService.isLoneWorkerEnabled() &&
            !this._userRolesService.getUserRole(UserRoles.Lone_Worker_Reports),
        });

        menuArray.push(obj);
        continue;
      }

      if (this.operationIconMap[menuData[i].operation]) {
        renderItem.title = menuData[i].name;

        if (menuData[i].operation == 'OPENJOBLIST') {
          renderItem.link = '/jobs';
          renderItem.queryParams = menuData[i].autoRefresh ? { autorefresh: true } : undefined;
        } else if (menuData[i].operation == 'OPENSCHEDULE') {
          renderItem.link = '/schedule/' + menuData[i].name.replace(' ', '-').toLowerCase();
          renderItem.queryParams = menuData[i].autoRefresh ? { autorefresh: true } : undefined;
        } else if (menuData[i].operation == 'OPENNOTIFICATIONHISTORY') {
          renderItem.link = '/notificationhistory';
          renderItem.queryParams = menuData[i].autoRefresh ? { autorefresh: true } : undefined;
        } else if (menuData[i].operation == 'OPENMEDIAMANAGEMENT') {
          renderItem.link = '/mediamanagement';
        } else if (menuData[i].operation == 'OPENMOBILEUSERSLIST') {
          renderItem.queryParams = menuData[i].autoRefresh ? { autorefresh: true } : undefined;
          renderItem.link = '/users-list';
        } else {
          const [link, params] = this.getRouterLink(menuData[i]);
          renderItem.link = link;
          renderItem.queryParams = params;
        }

        renderItem.operation = menuData[i].operation;
        menuArray.push(renderItem);
      } else {
        const startMenu = menuList.find((x) => x.menuId == menuData[i].object);
        const renderParentItem: RenderMenuItem = {
          id: 'dashboards.project',
          title: menuData[i].name,
          icon: 'folder',
          children: [],
          link: '',
          type: 'collapsable',
          operation: menuData[i].operation,
        };
        if (startMenu != null && menuData != null) {
          renderParentItem.children = this.GenerateMenu(startMenu.menuData, menuList);
          menuArray.push(renderParentItem);
        }
      }
    }

    return menuArray;
  }

  private base64UrlEncode(str: string): string {
    const base64 = btoa(str);
    return base64
      .replace(/\+/g, '-') // Replace + with -
      .replace(/\//g, '_') // Replace / with _
      .replace(/=+$/, ''); // Remove trailing =
  }

  private getRouterLink(selectedData: MenuItemModel) {
    let link = '';
    switch (selectedData.operation) {
      case 'OPENFORM':
        link = '/forms/' + selectedData.object + '/completedrecords';
        break;
      case 'OPENFORMSLIST':
        link = '/forms';
        break;
      case 'OPENMAP':
        link = '/jobs/jobmapview';
        break;
      case 'OPENREPORT':
        link = '/reports/' + selectedData.object;
        break;
      case 'OPENEXTERNALWEBPAGE':
        link = '/external/' + this.base64UrlEncode(selectedData.object);
        break;
      case 'OPENCATALOGUE':
        link = '/catalogues/view/' + selectedData.object;
        break;
      default:
        link = '/forms/view/' + selectedData.object;
    }

    const result: [link: string, params?: object] = [link];
    if (selectedData.autoRefresh) result[1] = { autorefresh: true };
    return result;
  }
}

export default MenuService;

export interface RenderedMenu {
  menu: MenuModel[];
  customMenu: boolean;
  root: FuseNavigationItem;
}

export interface RenderMenuItem extends FuseNavigationItem {
  id: string;
  title: string;
  icon: string;
  children: Array<any>;
  link: string;
  operation: string;
}
